// Apolo IDE — useTaskPanel (S2 Task Layer E3-WS1-T02).
//
// Thin adapter layer over useDecomposer that converts the raw plan returned by
// /api/agents/decompose into a typed Task[] (defined in S2 arch baseline).
//
// Does NOT import from apolo-api/ — spec is small enough to inline.
//
// Shape returned:
//   tasks           : Task[]  (empty while plan not loaded)
//   planId          : string | null
//   busy            : boolean
//   phase           : same as useDecomposer phases
//   error           : string | null
//   decompose       : async ({ message, sessionId }) => { ok, error?, data? }
//   approve         : async () => { ok, error?, data? }
//   reject          : async () => { ok, error?, data? }
//   reset           : () => void
//   selectedTaskId  : string | null
//   setSelectedTaskId : (id: string | null) => void
//   verifyResults   : Map<string, verifyResult>  (always empty in S2; S4+ fills it)

import { useCallback, useMemo, useState } from 'react';
import useDecomposer from './useDecomposer.js';

/**
 * Convert a SubagentSpec from the decomposer API into a frontend Task object.
 * spec_ref / checkpoint are reserved for S3/S4 and always null here.
 *
 * @param {object} spec   — shape from /api/agents/decompose plan.subagents[]
 * @param {string} parentDispatchId
 * @param {string|null} sessionId
 * @returns {import('../lib/taskTypes.js').Task}
 */
function specToTask(spec, parentDispatchId, sessionId) {
  return {
    id: spec.id ?? Math.random().toString(36).slice(2, 10),
    parentDispatchId,
    sessionId: sessionId ?? null,
    goal: spec.goal ?? spec.task ?? '(sem goal)',
    model: spec.model ?? 'unknown',
    tools: Array.isArray(spec.tools) ? [...spec.tools] : [],
    status: 'proposed',
    estimatedCostUsd: spec.maxCostUsd ?? spec.estimatedCostUsd ?? 0,
    canParallelize: spec.canParallelize ?? false,
    spec_ref: null,    // RESERVED S3/S4
    checkpoint: null,  // RESERVED S3/S4
  };
}

/**
 * @param {{ sessionId?: string, decompose?: Function, approve?: Function, reject?: Function }} options
 */
export default function useTaskPanel(options = {}) {
  const decomposer = useDecomposer(options);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  // S4+ will populate verifyResults; in S2 it's always an empty Map.
  const verifyResults = useMemo(() => new Map(), []);

  // Convert plan.subagents → Task[] every time the plan object changes.
  const tasks = useMemo(() => {
    const plan = decomposer.plan;
    if (!plan) return [];
    const planId = decomposer.planId ?? plan.planId ?? 'unknown';
    const specs = plan.subagents ?? plan.agents ?? [];
    return specs.map((s) => specToTask(s, planId, options.sessionId ?? null));
  }, [decomposer.plan, decomposer.planId, options.sessionId]);

  const decompose = useCallback(
    ({ message, sessionId }) => decomposer.decompose({ message, sessionId }),
    [decomposer],
  );

  return {
    // passthrough from useDecomposer
    planId: decomposer.planId,
    busy: decomposer.busy,
    phase: decomposer.phase,
    error: decomposer.error,
    budget: decomposer.budget,
    approve: decomposer.approve,
    reject: decomposer.reject,
    reset: decomposer.reset,
    // task layer
    tasks,
    decompose,
    selectedTaskId,
    setSelectedTaskId,
    verifyResults,
  };
}
